import { z } from 'zod';

/*
 * Add-user form schema (F02 R16). The three things a new account requires are a
 * name, an email and a role (AC-ce9ab9a7, Identity-enforced; the form refuses
 * client-side as the courtesy half). Crucially there is NO default role
 * (AC-79b3d5d7): `role` is a nullable discriminator with a `null` initial value
 * the schema rejects, so the form cannot be submitted until a role is chosen
 * deliberately. The administrator privilege defaults to unticked (AC-4aac3d04),
 * expressed as `isAdministrator` defaulting to false in the form's initial
 * values, never preset true.
 *
 * `role` is validated against the two roles the domain fixes; which of them the
 * CHOOSER OFFERS is decided by the org kind (AC-23bf376f) in
 * `../role-options.ts`, not here — the schema only guarantees a role was chosen.
 */
export const addUserFormSchema = z.object({
  displayName: z.string().min(1, 'Name is required.'),
  email: z.string().min(1, 'Email is required.').email('Enter a valid email.'),
  // No default: the initial value is null and null fails the enum, so a role
  // must be chosen deliberately before the form can be saved (AC-79b3d5d7).
  role: z
    .enum(['tpabilling', 'client'], { message: 'Choose a role.' })
    .nullable()
    .refine((v): v is 'tpabilling' | 'client' => v !== null, {
      message: 'Choose a role.',
    }),
  // Privilege off unless set deliberately (AC-4aac3d04): defaults to false in
  // the form's initial values and is only ever true when the person ticks it.
  isAdministrator: z.boolean(),
});

/*
 * The form's working value shape, where `role` may be null (nothing chosen yet)
 * so the dialog can start with no role preselected.
 */
export interface AddUserFormValues {
  displayName: string;
  email: string;
  role: 'tpabilling' | 'client' | null;
  isAdministrator: boolean;
}
